import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { Field, TextArea, TextInput } from '@/components/Field'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { VoiceMemoInput } from '@/components/VoiceMemoInput'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { payloadValue, useEditableEvent } from '@/lib/editEvent'
import { uploadAttachment } from '@/lib/attachments'

export function NoteLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const edit = useEditableEvent(['note.created'])
  const [searchParams] = useSearchParams()

  const isFirstPhoto = searchParams.get('first_photo') === '1'
  const babyName = searchParams.get('name') ?? ''

  const [text, setText] = useState(isFirstPhoto ? `${babyName || 'Baby'}'s first photo — welcome to the world.` : '')
  const [category, setCategory] = useState(isFirstPhoto ? 'milestone' : '')
  const [occurredAt, setOccurredAt] = useState<string>(toLocalInputValue(new Date()))
  const [photos, setPhotos] = useState<File[]>([])
  const [voiceMemo, setVoiceMemo] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    setText(payloadValue<string>(p, 'text') ?? '')
    setCategory(payloadValue<string>(p, 'category') ?? '')
    setOccurredAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  if (!person || !familyId || !user) return <Screen title="Note" back />

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return
      if (!text.trim() && photos.length === 0 && !voiceMemo) throw new Error('Write something, record a voice memo, or attach a photo')

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType: 'note.created',
        payload: {
          text: text.trim(),
          ...(category.trim() ? { category: category.trim() } : {}),
          ...(photos.length > 0 ? { has_attachment: true, photo_count: photos.length } : {}),
        } as import('@/lib/types').JSONValue,
        occurredAt: new Date(occurredAt),
        supersedes: edit.supersedesId,
      })

      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }
      if (voiceMemo) {
        await uploadAttachment({ familyId, userId: user.id, file: voiceMemo, eventId })
      }

      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const isEditing = !!edit.original
  return (
    <Screen title={isEditing ? 'Edit note' : (isFirstPhoto ? 'First photo' : 'Note')} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {isFirstPhoto && !isEditing && (
          <div
            className="rounded-r-sm p-4"
            style={{ background: `linear-gradient(180deg, ${PACK_COLORS.pregnancy.light}18, transparent), #141B19`, borderTop: `1px solid ${PACK_COLORS.pregnancy.light}55` }}
          >
            <Kicker color={PACK_COLORS.pregnancy.light}>Welcome moment</Kicker>
            <p className="mt-2 text-body text-text-secondary">
              Attach the first photo of {babyName || 'baby'}. This becomes the first entry in the timeline — a note you'll come back to years from now.
            </p>
          </div>
        )}
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{edit.error}</div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Editing an earlier note. Saving creates a new entry that supersedes the original.
          </div>
        )}
        <Field label="What happened">
          <TextArea
            rows={5}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="First smile. First tooth. First time saying da-da. Or the small thing you want to remember tomorrow."
          />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos + video (optional)"
          hint={isFirstPhoto ? "Attach the first photo — this becomes the first entry in the timeline." : "First smile, first tooth, mood shot — up to 4 photos/videos."}
        />

        <VoiceMemoInput memo={voiceMemo} onChange={setVoiceMemo} />

        <Field label="Category (optional)" hint="milestone, mood, symptom, funny, other…">
          <TextInput value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. milestone" />
        </Field>
        <Field label="When">
          <TextInput type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>
        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}
        <Button type="submit" variant="primary" size="lg" packColor={PACK_COLORS.needs.light} loading={busy} className="w-full">
          {isEditing ? 'Save edit' : (photos.length > 0 ? `Save note + ${photos.length} photo${photos.length === 1 ? '' : 's'}` : 'Save note')}
        </Button>
      </form>
    </Screen>
  )
}
